import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Building2, Copy, Check, Mail, CheckCircle } from 'lucide-react';
import { authAPI } from '../utils/api';
import AddressAutocomplete from '../components/ui/address-autocomplete';

const RegisterCompanyPage = ({ setUser }) => {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    company_city: '',
    company_country: 'UK',
    company_postal_code: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_password: '',
  });
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [copiedCodes, setCopiedCodes] = useState({});
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification');
      return;
    }
    
    setLoading(true);

    try {
      const response = await authAPI.registerCompany({
        ...formData,
        recaptcha_token: recaptchaToken
      });
      
      // Check if email verification is required (new flow)
      if (response.data.requires_verification) {
        setRegisteredEmail(response.data.email);
        setRegistrationComplete(true);
        toast.success('Registration successful! Please check your email to verify your account.');
      } else if (response.data.token) {
        // Old flow (shouldn't happen with updated backend, but handle gracefully)
        const { token, user } = response.data;
        localStorage.setItem('movemate_token', token);
        localStorage.setItem('movemate_user', JSON.stringify(user));
        setUser(user);
        toast.success('Company created successfully!');
        navigate('/control-panel');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      // Reset reCAPTCHA on error
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      const response = await authAPI.enable2FA({
        email: twoFAData.admin_email,
        verification_code: verificationCode
      });

      const { token, user } = response.data;
      localStorage.setItem('movemate_token', token);
      localStorage.setItem('movemate_user', JSON.stringify(user));
      setUser(user);

      toast.success('2FA enabled! Redirecting to dashboard...');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      toast.error('Invalid verification code');
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedCodes({ ...copiedCodes, [index]: true });
    setTimeout(() => setCopiedCodes({ ...copiedCodes, [index]: false }), 2000);
  };

  // Show verification required screen after successful registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 py-12" data-testid="verification-required-page">
        <div className="w-full max-w-md">
          <Card className="glassmorphism border-2 border-white/50 shadow-xl">
            <CardHeader className="text-center pb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-green-700">Check Your Email</CardTitle>
              <CardDescription className="text-base mt-2">
                We've sent a verification link to:
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-semibold text-lg">{registeredEmail}</p>
              </div>
              
              <div className="space-y-3 text-sm text-gray-600">
                <p>Click the link in your email to verify your account and activate your 14-day White Label trial.</p>
                <p className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  50 free AI credits included
                </p>
              </div>

              <div className="pt-4 border-t space-y-3">
                <p className="text-sm text-gray-500">
                  Didn't receive the email? Check your spam folder or
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    toast.info('Please try logging in - you can request a new verification email from the login page.');
                    navigate('/login');
                  }}
                >
                  Go to Login Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-12" data-testid="register-company-page">
      <div className="w-full max-w-2xl">
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="MoveMate" className="h-12" />
            <h1 className="text-3xl font-bold gradient-text">MoveMate</h1>
          </div>
        </div>

        <Card className="glassmorphism border-2 border-white/50 shadow-xl">
          <CardHeader className="text-center pb-6">
            <CardTitle className="text-3xl font-bold gradient-text">Register Your Company</CardTitle>
            <CardDescription className="text-base mt-2">Start managing your removal business</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-company-form">
              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Company Information</h3>
                <div>
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    data-testid="company-name-input"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Company Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="company-email-input"
                      value={formData.company_email}
                      onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="company-phone-input"
                      value={formData.company_phone}
                      onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="address">Address *</Label>
                  <AddressAutocomplete
                    value={formData.company_address}
                    onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                    onAddressSelect={(addr) => setFormData({
                      ...formData,
                      company_address: addr.street || addr.formatted_address || '',
                      company_city: addr.city || formData.company_city,
                      company_postal_code: addr.postal_code || formData.company_postal_code,
                      company_country: addr.country || formData.company_country
                    })}
                    placeholder="Start typing your address..."
                    data-testid="company-address-input"
                  />
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      data-testid="company-city-input"
                      value={formData.company_city}
                      onChange={(e) => setFormData({ ...formData, company_city: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code">Postal Code *</Label>
                    <Input
                      id="postal_code"
                      data-testid="company-postal-input"
                      value={formData.company_postal_code}
                      onChange={(e) => setFormData({ ...formData, company_postal_code: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      data-testid="company-country-input"
                      value={formData.company_country}
                      onChange={(e) => setFormData({ ...formData, company_country: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Admin Account */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="text-lg font-semibold">Admin Account</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="admin_first_name">First Name *</Label>
                    <Input
                      id="admin_first_name"
                      data-testid="admin-first-name-input"
                      value={formData.admin_first_name}
                      onChange={(e) => setFormData({ ...formData, admin_first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_last_name">Last Name *</Label>
                    <Input
                      id="admin_last_name"
                      data-testid="admin-last-name-input"
                      value={formData.admin_last_name}
                      onChange={(e) => setFormData({ ...formData, admin_last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="admin_email">Admin Email *</Label>
                  <Input
                    id="admin_email"
                    type="email"
                    data-testid="admin-email-input"
                    value={formData.admin_email}
                    onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="admin_password">Admin Password *</Label>
                  <Input
                    id="admin_password"
                    type="password"
                    data-testid="admin-password-input"
                    value={formData.admin_password}
                    onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* reCAPTCHA */}
              <div className="flex justify-center pt-4" data-testid="recaptcha-container">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                  onExpired={handleRecaptchaExpired}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={loading || !recaptchaToken}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating Company...' : 'Create Company'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-primary font-semibold hover:underline">
                  Login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter the 6-digit code sent to your email
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Backup Codes */}
            {twoFAData?.backup_codes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Save Your Backup Codes</h4>
                <p className="text-sm text-yellow-800 mb-4">
                  Store these codes safely. You can use them to access your account if you lose access.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {twoFAData.backup_codes.map((code, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                      <code className="text-sm flex-1">{code}</code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(code, index)}
                      >
                        {copiedCodes[index] ? <Check size={16} /> : <Copy size={16} />}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Verification */}
            <div>
              <Label htmlFor="verification_code">Enter 6-Digit Code</Label>
              <Input
                id="verification_code"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={6}
              />
            </div>

            <Button
              onClick={handleEnable2FA}
              className="w-full"
              disabled={verificationCode.length !== 6}
            >
              Enable 2FA & Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegisterCompanyPage;
